











import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import Navbar from "@/components/navbar/navbar.vue";
import AppSettings from "@/appsettings";
import UnsupportedBrowser from "@/components/browser/unsupported.vue";
import ConnectionMessage from "@/components/app/connection-message.vue";
import User from "./models/user";
import { mapState, mapGetters } from "vuex";
import { Api } from "./config";
import { SET_TOKEN } from "./store/mutation-types";
import Jwt from "./models/jwt";

@Component({
  components: {
    ConnectionMessage,
    Navbar,
    UnsupportedBrowser,
  },
  computed: {
    ...mapGetters("authentication", ["isAuthenticated"]),
    ...mapState("authentication", ["user", "token"]),
  },
})
export default class App extends Vue {
  loading = true;
  user!: User;
  token!: Jwt;
  isAuthenticated!: boolean;

  //
  // If any error occurs when
  // calling our API, it is checked for
  // a 401 bad request error. If this is the
  // case: a call for a refreshtoken exchange is made
  createApiInterceptors() {
    Api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const originalRequest = error.config;
        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          //
          // Don't try to refresh a token
          // if none is found...
          if (!this.token || !this.token.refreshToken) {
            return Api(originalRequest);
          }

          return Api.post("/auth/refreshtoken", {
            accessToken: this.token.accessToken.token,
            refreshToken: this.token.refreshToken,
          }).then((response) => {
            if (!response) {
              this.$store.dispatch("authentication/logout").then(() => {
                this.$router.push({ name: "login" });
              });

              return Api(originalRequest);
            }

            if (response.status === 200) {
              const jwt = new Jwt(
                response.data.accessToken,
                response.data.refreshToken
              );

              this.$store.commit(`authentication/${SET_TOKEN}`, jwt);

              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${jwt.accessToken.token}`;
              return Api.request(originalRequest);
            }
          });
        }
      }
    );
  }

  //
  // Load all required data
  // - get the user
  //   - set the authorization if a user has been found
  // - get subscriptions
  // - get settings
  async getRequiredData() {
    //
    // Don't try to load data
    // if there's no JWT token
    if (!this.token) {
      return;
    }

    try {
      //
      // Get the user
      await this.$store.dispatch("authentication/getUser");
      //
      // Get the subscriptions
      await this.$store.dispatch("subscriptions/getSubscriptions");
      //
      // Get the settings for this user
      await this.$store.dispatch("settings/get");
    } catch {
      // do nothing
    }
  }

  //
  // Watch changes to $route
  // - Change the title of the browser
  // - Track pageview in Google Analytics
  @Watch("$route")
  onRouteChange(to: any) {
    document.title = `${to.meta.title} – Urlcheckr`;

    let toPath = to.path;
    // Avoid saving url id's
    if (to.name === "UrlDetail") {
      toPath = "/project/url/detail";
    }

    // Track pageview in GA
    this.$gtag.pageview({
      // eslint-disable-next-line @typescript-eslint/camelcase
      page_path: toPath,
    });
  }

  async mounted() {
    this.loading = true;

    Vue.nextTick(() => {
      // @ts-ignore
      // eslint-disable-next-line no-undef
      Paddle.Setup({
        vendor: AppSettings.VendorId,
      });
    });

    //
    // Set up interceptors for our api
    this.createApiInterceptors();

    //
    // Get all required data
    await this.getRequiredData();

    this.loading = false;
  }
}
